import { useTranslation } from "react-i18next";
import { currentLocale, LOCALE_NAMES, setLocale, SUPPORTED_LOCALES, type Locale } from "../../lib/i18n";

/**
 * The language picker: a globe that opens the 10 GMS languages, each written
 * as its own endonym with its own `lang` attribute — the list must be legible
 * to someone who just landed in the wrong language, so no name is translated.
 *
 * daisyUI focus-driven dropdown, same mechanics as the sidebar ProfileCard:
 * the menu is always a light base-100 card; only the trigger adapts to its
 * surface. `variant="onblue"` sits in the brand-blue sidebar footer,
 * `variant="light"` on the login page, `variant="bar"` in the shell's top bar
 * — a 40px round navy-soft globe, the slot (and look) of the prototype's
 * top-bar round button; the language name is its tooltip and the list.
 */
export function LanguageSwitcher({
	variant = "light",
	dropdownClass = "dropdown-end",
}: {
	variant?: "onblue" | "light" | "bar";
	dropdownClass?: string;
}) {
	const { t } = useTranslation("common");
	const active = currentLocale();

	const pick = (locale: Locale) => {
		void setLocale(locale);
		// Focus-driven dropdown: blurring the focused element closes the menu.
		if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
	};

	const trigger =
		variant === "onblue"
			? "text-white hover:bg-white/10 focus-visible:ring-white/80"
			: "text-base-content/70 hover:bg-base-200 hover:text-base-content focus-visible:ring-gms-blue/60";

	return (
		<div className={`dropdown ${dropdownClass}`}>
			{variant === "bar" ? (
				<button
					type="button"
					tabIndex={0}
					aria-label={t("language.switch")}
					title={`${t("language.switch")} · ${LOCALE_NAMES[active]}`}
					className="grid size-[2.5rem] shrink-0 cursor-pointer place-items-center rounded-full bg-gms-navy-soft text-gms-navy outline-none transition-colors duration-150 hover:bg-gms-surface-2 focus-visible:ring-2 focus-visible:ring-gms-navy/40"
				>
					<GlobeIcon className="size-[1.1875rem]" />
				</button>
			) : (
				<button
					type="button"
					tabIndex={0}
					aria-label={t("language.switch")}
					title={t("language.switch")}
					className={`flex cursor-pointer items-center gap-[0.375rem] rounded-field px-[0.5rem] py-[0.375rem] text-[0.8125rem] font-semibold outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-inset ${trigger}`}
				>
					<GlobeIcon className="size-[1.125rem] shrink-0" />
					<span lang={active}>{LOCALE_NAMES[active]}</span>
				</button>
			)}

			<ul
				tabIndex={-1}
				className="dropdown-content z-20 my-[0.5rem] max-h-[70vh] w-[13rem] overflow-y-auto rounded-box border border-base-300 bg-base-100 py-[0.375rem] text-base-content shadow-[0_12px_32px_rgba(16,24,40,0.18)]"
			>
				{SUPPORTED_LOCALES.map((locale) => (
					<li key={locale}>
						<button
							type="button"
							lang={locale}
							aria-current={locale === active || undefined}
							onClick={() => pick(locale)}
							className="flex w-full cursor-pointer items-center gap-[0.625rem] px-[1rem] py-[0.5rem] text-left text-[0.875rem] font-medium transition-colors hover:bg-base-200 focus-visible:bg-base-200 focus-visible:outline-none aria-[current]:font-bold aria-[current]:text-gms-blue"
						>
							<span className="min-w-0 flex-1 truncate">{LOCALE_NAMES[locale]}</span>
							{locale === active && <CheckIcon className="size-[1rem] shrink-0" />}
						</button>
					</li>
				))}
			</ul>
		</div>
	);
}

function GlobeIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden className={className}>
			<circle cx="12" cy="12" r="9" />
			<path d="M3 12h18M12 3c2.5 2.6 3.75 5.6 3.75 9S14.5 18.4 12 21c-2.5-2.6-3.75-5.6-3.75-9S9.5 5.6 12 3Z" />
		</svg>
	);
}

function CheckIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden className={className}>
			<path d="m5 12.5 4.5 4.5L19 7.5" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}
